import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain } from 'lucide-react';
import { IntelligenceCard, IntelligenceCardStatus } from './IntelligenceCard';
import { usePaymentHealth } from '@/hooks/usePaymentHealth';
import { useBillAssistData } from '@/hooks/useBillAssistData';

export const PaymentHealthCard: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = usePaymentHealth();
  const { data: billAssistData } = useBillAssistData();

  // Determine status based on overdue payments
  const getStatus = (count: number): IntelligenceCardStatus => {
    if (count === 0) return 'success';
    if (count <= 2) return 'warning';
    return 'danger';
  };

  // Handle Bill Assist action
  const handleOpenBillAssist = () => {
    // Navigate to invoices page filtered to overdue and partial payments
    navigate('/admin/invoices?filter=overdue,partial');
  };

  const count = data?.count || 0;
  const totalOverdue = data?.total_overdue_amount || 0;
  const status = getStatus(count);

  // Build enhanced context text with Bill Assist intelligence
  const getContextText = () => {
    const totalInsights = billAssistData?.metrics.total_items || 0;
    const criticalCount = billAssistData?.metrics.critical_count || 0;
    const paymentFailures = billAssistData?.smartCards.find(card => card.category === 'payment_failures');
    
    if (count === 0 && totalInsights === 0) {
      return "All payments healthy! AI monitoring for early indicators 🧠";
    }
    
    if (totalInsights > 0) {
      if (criticalCount > 0) {
        return `${criticalCount} critical issues detected - Bill Assist recommends immediate action`;
      }
      
      if (paymentFailures && paymentFailures.count > 0) {
        return `${paymentFailures.count} payment failures + ${count} overdue - Smart resolution available`;
      }
      
      return `${totalInsights} payment insights ready - AI confidence ${Math.round((billAssistData?.metrics.avg_confidence || 0) * 100)}%`;
    }
    
    if (count === 1) {
      const payment = data?.overdue_payments[0];
      return `${payment?.family_name} - $${payment?.amount_due} (${payment?.days_overdue} days overdue) - Smart actions available`;
    }
    
    return `$${totalOverdue.toFixed(2)} at risk across ${count} families - AI recommendations ready`;
  };

  // Build enhanced status text with financial impact
  const getStatusText = () => {
    if (count === 0) return 'All current';
    if (count === 1) return `1 overdue – $${totalOverdue.toFixed(2)} at risk`;
    return `${count} overdue – $${totalOverdue.toFixed(2)} at risk`;
  };

  return (
    <IntelligenceCard
      title="Bill Assist: Payment Health Monitor"
      description="Smart payment intelligence"
      icon={Brain}
      metric={count}
      metricLabel={count === 1 ? "overdue payment" : "overdue payments"}
      contextText={getContextText()}
      status={status}
      statusText={getStatusText()}
      actionLabel="Open Bill Assist"
      onAction={handleOpenBillAssist}
      actionDisabled={false} // Always allow access to hub
      isLoading={isLoading}
      error={error?.message}
    />
  );
};